import { Injectable } from '@angular/core';
import { AngularFaviconService } from 'angular-favicon';
import { DataTransferService } from './data-transfer.service';
import * as WebFont from 'webfontloader';

/** `LogoImage` object stored in localStorage after login / branding API. */
export interface LogoImageStorage {
    readonly logo_image?: string;
    readonly fav_image?: string;
    readonly preference_setting?: readonly BrandingPreferenceSetting[];
}

/** One entry from {@link LogoImageStorage.preference_setting} in localStorage. */
export interface BrandingPreferenceSetting {
    readonly name?: string;
    readonly value?: string;
    readonly type?: string;
    readonly display_name?: string;
}

/**
 * Maps {@link BrandingPreferenceSetting.name} to canonical `--new-*` CSS variables.
 * Values are persisted at runtime; components adopt tokens as needed.
 */
export const NEW_MENU_PREFERENCE_COLOR_MAP: Readonly<Record<string, string>> = {
    primary: '--new-primary-color',
    secondary: '--new-secondary-color',
    body_text: '--new-body-text-color',
    link_text: '--new-link-text-color',
    success: '--new-success-color',
    warning: '--new-warning-color',
    error: '--new-error-color',
    bg_grey: '--new-bg-grey-color',
    outlines_dividers: '--new-outlines-dividers-color',
    bg_white: '--new-bg-white-color',
    bg_scrollbar: '--new-bg-scrollbar-color',
    main_navigation_bg: '--new-main-navigation-bg-color',
    navigation_icon_selection: '--new-navigation-icon-selection-color',
    primary_button_bg: '--new-primary-button-bg-color',
    primary_button_text: '--new-primary-button-text-color',
    primary_button_hover_bg: '--new-primary-button-hover-bg-color',
    secondary_button_outline: '--new-secondary-button-outline-color',
    secondary_button_text: '--new-secondary-button-text-color',
    clickable_icons: '--new-clickable-icons-color',
    non_clickable_icons: '--new-non-clickable-icons-color'
};

/** Font preferences from {@link BrandingPreferenceSetting.name}. */
export const NEW_MENU_PREFERENCE_FONT_MAP: Readonly<Record<string, string>> = {
    default_font_family: '--new-default-font-family',
    brand_font_family: '--new-brand-font-family'
};

/** Image preferences from {@link BrandingPreferenceSetting.name}. */
export const NEW_MENU_PREFERENCE_IMAGE_MAP: Readonly<Record<string, string>> = {
    logo: '--new-logo-url'
};

/** CSS variable for the new menu logo (`LogoImage.logo_image` in localStorage). */
export const NEW_MENU_LOGO_IMAGE_CSS_VAR = '--new-logo-url';

export const DEFAULT_FAVICON_PATH = './assets/images/oxm.png';

@Injectable({
    providedIn: 'root'
})
export class BrandingService {
    constructor(
        private dataTransferService: DataTransferService,
        private readonly ngxFavicon: AngularFaviconService
    ) {}

    loadFont(fontName) {
        WebFont.load({
            google: {
                families: [fontName + ':400,500,600,i']
            }
        });
    }

    setBrandingValues(data) {
        this.loadFont('Open Sans');
        data.forEach((element) => {
            if (element) {
                if (element.type == 'image') {
                    this.dataTransferService.sendBrandingLogo(element.value);
                } else if (element.type == 'font') {
                    const fieldName =
                        '--' +
                        element.display_name.split(' ').join('-').toLowerCase();
                    this.applyFontPreference(fieldName, element.value);
                } else {
                    let fieldName =
                        '--' +
                        element.display_name.split(' ').join('-').toLowerCase();
                    fieldName = fieldName.replace('/', '-');
                    fieldName += '-color';
                    document.documentElement.style.setProperty(
                        fieldName,
                        element.value
                    );
                }
            }
        });
        this.applyNewMenuBrandingFromPreferences(data);
        this.dataTransferService.setDashboardColors();
    }

    applyBrandingFromLogoImageStorage(
        logoImage: LogoImageStorage | null | undefined
    ): void {
        this.applyNewMenuBrandingFromPreferences(logoImage?.preference_setting);
        this.applyNewMenuLogoImageFromStorage(logoImage);
        this.applyFaviconFromLogoImage(logoImage);
    }

    /**
     * Persists all white-label tokens from {@link LogoImageStorage.preference_setting}
     * onto `:root` for progressive adoption in new UI components.
     */
    applyNewMenuBrandingFromPreferences(
        preferences: readonly BrandingPreferenceSetting[] | null | undefined
    ): void {
        if (!preferences?.length) {
            return;
        }

        Object.entries(NEW_MENU_PREFERENCE_COLOR_MAP).forEach(
            ([preferenceName, cssVariable]) => {
                const color = this.getPreferenceValue(
                    preferences,
                    preferenceName,
                    'color'
                );
                if (color) {
                    this.setRootCssVar(cssVariable, color);
                }
            }
        );

        Object.entries(NEW_MENU_PREFERENCE_FONT_MAP).forEach(
            ([preferenceName, cssVariable]) => {
                const font = this.getPreferenceValue(
                    preferences,
                    preferenceName,
                    'font'
                );
                if (font) {
                    this.applyFontPreference(cssVariable, font);
                }
            }
        );

        Object.entries(NEW_MENU_PREFERENCE_IMAGE_MAP).forEach(
            ([preferenceName, cssVariable]) => {
                const imageUrl = this.getPreferenceValue(
                    preferences,
                    preferenceName,
                    'image'
                );
                if (imageUrl) {
                    this.setRootCssVar(cssVariable, imageUrl);
                }
            }
        );

    }

    /**
     * Sets `--new-logo-url` from {@link LogoImageStorage.logo_image} for the new menu only.
     */
    applyNewMenuLogoImageFromStorage(
        logoImage: LogoImageStorage | null | undefined
    ): void {
        const logoUrl = logoImage?.logo_image?.trim();
        if (logoUrl) {
            this.setRootCssVar(NEW_MENU_LOGO_IMAGE_CSS_VAR, logoUrl);
        }
    }

    /**
     * Sets the application favicon from {@link LogoImageStorage.fav_image} in localStorage.
     */
    applyFaviconFromLogoImage(logoImage: LogoImageStorage | null | undefined): void {
        const faviconUrl = logoImage?.fav_image?.trim() || DEFAULT_FAVICON_PATH;
        this.ngxFavicon.setFavicon(faviconUrl);
    }

    private getPreferenceValue(
        preferences: readonly BrandingPreferenceSetting[],
        preferenceName: string,
        expectedType: string
    ): string | undefined {
        const preference = preferences.find(
            (entry) =>
                entry?.name?.toLowerCase() === preferenceName?.toLowerCase() &&
                entry?.type === expectedType &&
                entry?.value?.trim()
        );

        return preference?.value?.trim() || undefined;
    }

    private setRootCssVar(variableName: string, value: string): void {
        document.documentElement.style.setProperty(variableName, value);
    }

    private applyFontPreference(cssVariable: string, font: string): void {
        const formatted = this.formatFontFamilyCssValue(font);
        this.loadFont(font);
        this.setRootCssVar(cssVariable, formatted);
    }

    /** Wraps font names for valid `font-family` tokens inside CSS variables. */
    private formatFontFamilyCssValue(fontFamily: string): string {
        const trimmed = fontFamily.trim();
        if (!trimmed) {
            return trimmed;
        }

        const isQuoted =
            (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
            (trimmed.startsWith('"') && trimmed.endsWith('"'));

        return isQuoted ? trimmed : `'${trimmed}'`;
    }
}
